import React, { FC, useCallback, useMemo, useState } from 'react';

import { BUTTON_TYPES, EvConfirm } from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from '../../../consts/dom-consts';
import { getUrlsFromCSV } from '../../../helpers/SeedsListHelper';
import { formatURL } from '../../../helpers/UrlNormalizingHelper';
import { ImportUrlPrompt } from '../import-url-prompt/ImportUrlPrompt';

import {
	APPROVE_BUTTON_TEXT,
	CONFIRM_BUTTON_TITLE,
	FILE_MAX_SIZE_TO_IMPORT
} from './PropertyImportUrlsButton.constants';

interface IPropertyImportUrlsButton {
	onConfirm: (closeModal: () => void, fileContent: string[]) => void;
	importing: boolean;
	validHeader?: string;
}

export const PropertyImportUrlsButton: FC<IPropertyImportUrlsButton> = ({
	onConfirm,
	importing,
	validHeader = ''
}) => {
	const [fileTooBig, setFileTooBig] = useState(false);
	const [content, setContent] = useState<string[]>([]);
	const validCount = useMemo(() => getUrlsFromCSV(content).validUrls.length, [content]);
	const errCount = content.length - validCount;
	const disableImportBtn = fileTooBig || validCount < 1;
	const showErrMsg = fileTooBig || errCount > 0;
	const fileReader = useMemo(() => new FileReader(), []);

	const triggerButtonProps = useMemo(
		() => ({
			type: BUTTON_TYPES.SECONDARY,
			title: CONFIRM_BUTTON_TITLE,
			disabled: importing,
			children: CONFIRM_BUTTON_TITLE
		}),
		[importing]
	);

	const isValidHeader = useCallback(
		(fileHeader): boolean => {
			return fileHeader.trim().toLocaleLowerCase() === validHeader.trim().toLocaleLowerCase();
		},
		[validHeader]
	);

	const handleFileRead = useCallback((): void => {
		const fileContentArr = fileReader.result
			.toString()
			.split(/\r\n|\r|\n/)
			.filter((line) => line) // remove empty lines
			.map((urlStr) => formatURL(urlStr));

		if (isValidHeader(fileContentArr[0])) {
			fileContentArr.shift();
		}

		setContent(fileContentArr);
	}, [fileReader.result, isValidHeader]);

	const onFileBrowse = useCallback(
		({ browsedFiles }): void => {
			setContent([]);
			const fileBrowsed = browsedFiles[0];
			const tooBig = fileBrowsed.size > FILE_MAX_SIZE_TO_IMPORT;
			setFileTooBig(tooBig);

			if (!tooBig) {
				fileReader.onloadend = handleFileRead;
				fileReader.readAsText(fileBrowsed);
			}
		},
		[fileReader, handleFileRead]
	);

	const handleClose = useCallback((closeModal?: () => void): void => {
		setFileTooBig(false);
		setContent([]);

		if (closeModal) {
			closeModal();
		}
	}, []);

	const handleConfirm = useCallback(
		(closeModal: () => void): void => {
			onConfirm(closeModal, content);
		},
		[onConfirm, content]
	);

	return (
		<EvConfirm
			appElement={SITE_SCANNER_APP_ID}
			title={CONFIRM_BUTTON_TITLE}
			promptMessage={
				<ImportUrlPrompt
					onFileBrowse={onFileBrowse}
					showErrMsg={showErrMsg}
					errCount={errCount}
					validCount={validCount}
					content={content}
				/>
			}
			approveButtonText={APPROVE_BUTTON_TEXT}
			disabled={disableImportBtn}
			onClose={handleClose}
			triggerButtonProps={triggerButtonProps}
			onConfirm={handleConfirm}
		/>
	);
};
